import React, { useEffect, useState } from 'react';
import { ShieldCheck, Lightbulb, HeartHandshake, HardHat, CheckCircle2, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { fetchPublicSettings, makeSectionCopyPicker } from '../services/publicContentService';

// Static bilingual content for the four Strategic Objectives pillars. Kept as
// a fallback in code so the section always renders even if the CMS payload is
// empty. Icons + gradients stay static — they're design tokens, not content.
const PILLARS = [
  {
    icon: ShieldCheck,
    gradient: 'from-emerald-400 via-teal-400 to-cyan-400',
    title_ar: 'الريادة والامتثال البيئي',
    title_en: 'Environmental Leadership and Compliance',
    items: [
      {
        ar: 'تطبيق أفضل الممارسات البيئية وفق شهادات الجودة والبيئة (ISO 9001 و ISO 14001).',
        en: 'Applying environmental best practices in accordance with quality and environmental management standards (ISO 9001 and ISO 14001).',
      },
      {
        ar: 'الالتزام بالمعايير الدولية والمحلية والامتثال لجميع القوانين واللوائح البيئية والتشريعات ذات الصلة.',
        en: 'Adhering to international and local standards, ensuring full compliance with all relevant environmental laws, regulations, and legislation.',
      },
      {
        ar: 'ضمان موثوقية التقارير والدراسات البيئية لتقديم بيانات وتحليلات دقيقة واحترافية لعملائنا.',
        en: 'Guaranteeing the reliability of environmental reports and studies to deliver precise, professional data and analysis to our clients.',
      },
    ],
  },
  {
    icon: Lightbulb,
    gradient: 'from-amber-400 via-orange-400 to-amber-500',
    title_ar: 'الابتكار والتميز العلمي',
    title_en: 'Innovation and Scientific Excellence',
    items: [
      {
        ar: 'تبني أحدث التقنيات الحديثة ومنهجيات القياس المتطورة لمتابعة الأداء البيئي للمشروعات.',
        en: 'Adopting cutting-edge technologies and advanced measurement methodologies to monitor the environmental performance of projects.',
      },
      {
        ar: 'التقييم الذاتي للشركة وتشجيع ثقافة الابتكار لتقديم حلول غير تقليدية ومستدامة.',
        en: 'Conducting corporate self-assessments and fostering a culture of innovation to provide non-traditional, sustainable solutions.',
      },
      {
        ar: 'بناء شراكات استراتيجية مع المؤسسات البحثية والمنظمات البيئية لتعزيز الممارسات المستدامة.',
        en: 'Building strategic partnerships with research institutions and environmental organizations to enhance sustainable practices.',
      },
    ],
  },
  {
    icon: HeartHandshake,
    gradient: 'from-sky-400 via-blue-400 to-indigo-400',
    title_ar: 'الأثر الوطني والمسؤولية المجتمعية',
    title_en: 'National Impact and Social Responsibility',
    items: [
      {
        ar: 'المساهمة الفاعلة في مستهدفات التحول الوطني نحو بيئة خضراء واستدامة الموارد.',
        en: 'Controlling an active contribution to national transformation goals towards a green environment and resource sustainability.',
      },
      {
        ar: 'نشر الوعي والثقافة البيئية والمشاركة في المبادرات المجتمعية لتعزيز مفهوم المسؤولية البيئية.',
        en: 'Spreading environmental awareness and culture, and participating in community initiatives to foster the concept of environmental responsibility.',
      },
      {
        ar: 'تفعيل العمل المشترك مع القطاعين الحكومي والخاص لتحقيق الأهداف البيئية المشتركة.',
        en: 'Activating collaborative efforts with both the public and private sectors to achieve shared environmental goals.',
      },
    ],
  },
  {
    icon: HardHat,
    gradient: 'from-emerald-400 via-teal-400 to-cyan-400',
    title_ar: 'السلامة والاستدامة التشغيلية',
    title_en: 'Operational Safety and Sustainability',
    items: [
      {
        ar: 'تنفيذ إجراءات وقائية صارمة لتقليل المخاطر المرتبطة بالأنشطة البيئية وتوفير بيئة عمل آمنة.',
        en: 'Implementing strict preventive procedures to mitigate risks associated with environmental activities and provide a safe working environment.',
      },
      {
        ar: 'تطوير خطط متكاملة لإدارة الكوارث البيئية وحالات الطوارئ لضمان استمرارية الأعمال بأمان.',
        en: 'Developing integrated plans for environmental disaster and emergency management to ensure safe business continuity.',
      },
      {
        ar: 'ضمان مواءمة ممارسات الصحة والسلامة المهنية مع الالتزام البيئي الراسخ للشركة.',
        en: "Ensuring the alignment of occupational health and safety practices with the company's deeply rooted environmental commitment.",
      },
    ],
  },
];

const StrategicGoals = ({ theme = 'light' }) => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const isDark = theme === 'dark';

  // CMS override — settings.strategic_goals.pillars is expected to be an array
  // shaped like PILLARS above (minus the icon reference). Missing values fall
  // back to the static defaults so a partial payload never breaks the layout.
  const [pillars, setPillars] = useState(PILLARS);
  const [sc, setSc] = useState({});
  useEffect(() => {
    let alive = true;
    fetchPublicSettings('strategic_goals').then((v) => {
      if (!alive) return;
      const list = Array.isArray(v?.pillars) ? v.pillars : null;
      if (!list || list.length === 0) return;
      const merged = list.slice(0, PILLARS.length).map((raw, i) => {
        const base = PILLARS[i] || PILLARS[PILLARS.length - 1];
        const rawItems = Array.isArray(raw.items) ? raw.items : [];
        const items = base.items.map((baseItem, j) => {
          const r = rawItems[j] || {};
          return { ar: r.ar || baseItem.ar, en: r.en || baseItem.en };
        });
        return {
          icon: base.icon,
          gradient: base.gradient,
          title_ar: raw.title_ar || base.title_ar,
          title_en: raw.title_en || base.title_en,
          items,
        };
      });
      if (merged.length > 0) setPillars(merged);
    });
    fetchPublicSettings('section_copy').then((v) => { if (alive) setSc(v || {}); });
    return () => { alive = false; };
  }, []);
  const sec = makeSectionCopyPicker(sc, isAr);

  return (
    <section
      id="strategic-goals"
      dir={isAr ? 'rtl' : 'ltr'}
      className="relative py-32 overflow-hidden transition-colors duration-700"
    >
      {/* Theme-aware base */}
      {isDark ? (
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-blue-950 to-slate-950" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-emerald-50/40 to-amber-50/40" />
      )}

      {/* Subtle dot-grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: isDark
            ? 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)'
            : 'radial-gradient(circle at 1px 1px, rgba(15,23,42,0.4) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />

      {/* Ambient glows */}
      <div
        className="absolute top-1/4 left-1/4 w-[480px] h-[480px] rounded-full blur-[120px] pointer-events-none"
        style={{ background: isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.15)' }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-[480px] h-[480px] rounded-full blur-[120px] pointer-events-none"
        style={{ background: isDark ? 'rgba(245,158,11,0.10)' : 'rgba(245,158,11,0.14)' }}
      />

      <div className="relative container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div
            className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 ${
              isDark
                ? 'bg-amber-500/10 border border-amber-500/30'
                : 'bg-amber-600/10 border border-amber-700/30'
            }`}
          >
            <Sparkles className={`w-4 h-4 ${isDark ? 'text-amber-300' : 'text-amber-700'}`} />
            <span
              className={`text-xs tracking-[0.3em] uppercase ${
                isDark ? 'text-amber-200' : 'text-amber-800'
              }`}
              data-testid="strategic-goals-eyebrow"
            >
              {sec('strategic_goals_eyebrow', isAr ? 'توجهاتنا' : 'Our Direction')}
            </span>
          </div>

          <h2
            className={`text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}
            data-testid="strategic-goals-heading"
          >
            {isAr ? (
              <>
                {sec('strategic_goals_heading_prefix', 'أهدافنا')}{' '}
                <span
                  className={`bg-clip-text text-transparent ${
                    isDark
                      ? 'bg-gradient-to-r from-emerald-300 via-cyan-300 to-amber-300'
                      : 'bg-gradient-to-r from-emerald-700 via-cyan-700 to-amber-700'
                  }`}
                >
                  {sec('strategic_goals_heading_highlight', 'الاستراتيجية')}
                </span>
              </>
            ) : (
              <>
                {sec('strategic_goals_heading_prefix', 'Our Strategic')}{' '}
                <span
                  className={`bg-clip-text text-transparent ${
                    isDark
                      ? 'bg-gradient-to-r from-emerald-300 via-cyan-300 to-amber-300'
                      : 'bg-gradient-to-r from-emerald-700 via-cyan-700 to-amber-700'
                  }`}
                >
                  {sec('strategic_goals_heading_highlight', 'Objectives')}
                </span>
              </>
            )}
          </h2>

          <p
            className={`text-base md:text-lg leading-relaxed max-w-3xl mx-auto ${
              isDark ? 'text-slate-300' : 'text-slate-600'
            }`}
            data-testid="strategic-goals-subtitle"
          >
            {sec('strategic_goals_subtitle', isAr
              ? 'أربع ركائز تقود نهجنا نحو أثر بيئي طويل الأمد — من الامتثال والابتكار إلى المسؤولية الوطنية وسلامة العمليات.'
              : 'Four pillars driving our path to lasting environmental impact — from compliance and innovation to national responsibility and operational safety.')}
          </p>
        </div>

        {/* Pillars — 2-column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {pillars.map((pillar, i) => {
            const Icon = pillar.icon;
            return (
              <article
                key={i}
                data-testid={`strategic-pillar-${i}`}
                className={`group relative overflow-hidden rounded-3xl p-7 lg:p-9 animate-fade-in-up
                            transition-all duration-500 cursor-default backdrop-blur-xl
                            hover:-translate-y-1 ${
                              isDark
                                ? 'bg-white/[0.03] border border-white/10 hover:bg-white/[0.06] hover:border-white/20'
                                : 'bg-white/85 border border-slate-200 hover:bg-white hover:border-slate-300 shadow-sm hover:shadow-2xl'
                            }`}
                style={{ animationDelay: `${i * 0.15}s` }}
              >
                {/* Top gradient bar */}
                <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r ${pillar.gradient} opacity-70`} />

                {/* Hover glow */}
                <div
                  className={`absolute -inset-px rounded-3xl bg-gradient-to-br ${pillar.gradient}
                              opacity-0 group-hover:opacity-[0.08] blur-2xl transition-opacity duration-500`}
                />

                <div className="relative">
                  {/* Icon + number */}
                  <div className={`flex items-start justify-between mb-6 ${isAr ? 'flex-row-reverse' : ''}`}>
                    <div
                      className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${pillar.gradient}
                                  flex items-center justify-center shadow-lg
                                  transition-all duration-500
                                  group-hover:scale-110 group-hover:rotate-6`}
                    >
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <span
                      className={`text-xs tracking-[0.3em] font-mono ${
                        isDark ? 'text-amber-300/70' : 'text-amber-700/80'
                      }`}
                    >
                      {String(i + 1).padStart(2, '0')} / {String(pillars.length).padStart(2, '0')}
                    </span>
                  </div>

                  {/* Pillar title */}
                  <h3
                    className={`text-2xl lg:text-3xl font-bold mb-4 tracking-tight ${
                      isDark ? 'text-white' : 'text-slate-900'
                    }`}
                    data-testid={`strategic-pillar-title-${i}`}
                  >
                    {isAr ? pillar.title_ar : pillar.title_en}
                  </h3>

                  {/* Accent underline */}
                  <div className={`h-0.5 w-12 mb-6 bg-gradient-to-r ${pillar.gradient}
                                  transition-all duration-500 group-hover:w-20`} />

                  {/* Bullet items */}
                  <ul className="space-y-4">
                    {pillar.items.map((item, j) => (
                      <li
                        key={j}
                        data-testid={`strategic-pillar-${i}-item-${j}`}
                        className={`flex gap-3 items-start ${isAr ? 'flex-row-reverse text-right' : 'text-left'}`}
                        style={{ direction: isAr ? 'rtl' : 'ltr' }}
                      >
                        <CheckCircle2
                          className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                            isDark ? 'text-emerald-300' : 'text-emerald-600'
                          }`}
                          strokeWidth={2.2}
                        />
                        <span
                          className={`text-[15px] leading-relaxed ${
                            isDark ? 'text-slate-300/90' : 'text-slate-700'
                          }`}
                        >
                          {isAr ? item.ar : item.en}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StrategicGoals;
